/**
 * Click ids that another vendor's tag already stored on this page.
 *
 * We capture click ids from the URL of the landing hit, which is the only
 * moment they exist there. That misses everybody whose first ad click happened
 * before our script was installed, everybody who landed on a page our script
 * was not on yet, and everybody whose first visit was on a day the tag manager
 * had us switched off. Google's own tag has been writing that same gclid into
 * `_gcl_aw` for ninety days all along, and reading it costs nothing.
 *
 * The parsers are pure and live here rather than in `cookies.ts`, so the format
 * assumptions can be tested. Guessing at a cookie format and being wrong is not
 * a crash: it is a click id that silently matches nothing at the platform,
 * which looks exactly like an ad that did not work.
 */

/**
 * The gclid out of one of Google's own `_gcl_*` cookies.
 *
 * Format: `GCL.<unix seconds>.<click id>`. The click id itself never contains a
 * dot, but rejoining the remainder is free and cannot truncate a value that one
 * day does.
 *
 * `_gcl_aw` holds a search click, `_gcl_dc` a display click, `_gcl_gb` the
 * privacy-preserving gbraid. Same shape, so one parser.
 */
export function parseGclCookie(value: string | null): string | null {
  if (!value) return null;
  const parts = value.split(".");
  if (parts.length < 3) return null;
  if (parts[0] !== "GCL") return null;
  const clickId = parts.slice(2).join(".");
  if (!clickId) return null;
  return clickId;
}

/**
 * The msclkid out of Microsoft's `_uetmsclkid` cookie.
 *
 * FORMAT NOT VERIFIED against Microsoft's documentation: the search budget for
 * the session was spent and the help-centre page 404s. What is known is that
 * the cookie carries the msclkid and that observed values are prefixed with
 * `_uet`. So the prefix is stripped when present, and the remainder has to look
 * like an msclkid (32 hex characters) before we will use it.
 *
 * The check is there to fail in the safe direction. If the real format turns
 * out to be something else, this returns null and we are where we were: no
 * recovered click id. Without the check we would send Bing a value it cannot
 * match, and a rejected conversion is far harder to notice than a missing one.
 * Listed in docs/TEST_DEBT.md until it can be checked against a live UET tag.
 */
const MSCLKID_PATTERN = /^[0-9a-f]{32}$/i;

export function parseUetCookie(value: string | null): string | null {
  if (!value) return null;
  let candidate = value;
  if (candidate.indexOf("_uet") === 0) candidate = candidate.substring(4);
  if (!MSCLKID_PATTERN.test(candidate)) return null;
  return candidate;
}

/**
 * Is this `_fbc` still worth sending?
 *
 * `_fbc` carries its own creation time: `fb.1.<millis>.<fbclid>`. We read the
 * cookie raw and passed on whatever was in it, and Meta drops a click id older
 * than its attribution window without saying so, which is the worst kind of
 * failure this codebase has: a delivery that reports success and matches
 * nothing.
 *
 * Ninety days, the same window the cookie itself is written with. Beyond that
 * the value cannot be attributed by anyone, so sending it only pollutes the
 * payload. An unparseable timestamp counts as usable: dropping a value we
 * simply failed to read would lose real matches over a formatting change.
 */
export function isUsableFbc(value: string | null, maxAgeDays: number, now: number): boolean {
  if (!value) return false;
  const parts = value.split(".");
  if (parts.length < 4) return true;
  const createdAt = Number(parts[2]);
  if (!Number.isFinite(createdAt) || createdAt <= 0) return true;
  const ageDays = (now - createdAt) / 86_400_000;
  return ageDays <= maxAgeDays;
}
